import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { createAnalyticsHelpers } from '../../lzapps_core_code/business/analytics';
import { GET_APP_NAME, BUNDLE_APP_NAME } from '../../app_constant';
import { AuthContext } from './AuthProvider';
import { BundleContext } from './BundleProvider';
import { registerBaseSuperProps } from '../utils/analyticsSuperProps';

export const AnalyticsContext = createContext();

export const AnalyticsProvider = ({ children }) => {
  const posthog = usePostHog();
  const { isSubscribe } = useContext(AuthContext) || {};
  const { selectedCertificate } = useContext(BundleContext) || {};
  const helpersRef = useRef(null);
  const lastIsPremiumRef = useRef(undefined);
  const lastIdentifiedUidRef = useRef(null);

  // Create helpers once using the PostHog client
  if (!helpersRef.current && posthog) {
    helpersRef.current = createAnalyticsHelpers(posthog);
  }

  // Register super properties after PostHog is fully initialized.
  // (Also re-run by AuthProvider after a sign-out reset() wipes them.)
  useEffect(() => {
    registerBaseSuperProps(posthog);
  }, [posthog]);

  // Persist UTM params from the URL onto the person profile so downstream
  // funnels can be cohorted by acquisition source (e.g. utm_content=isc2-hub
  // vs nav-signin) without a manual join on the entry event.
  // - utm_*  → $set (last-touch, overwrites on subsequent visits)
  // - first_utm_* → $set_once (first-touch, only set if missing)
  useEffect(() => {
    if (!posthog || typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
      const setProps = {};
      const setOnceProps = {};
      let hasUtm = false;
      for (const k of keys) {
        const v = params.get(k);
        if (v) {
          setProps[k] = v;
          setOnceProps[`first_${k}`] = v;
          hasUtm = true;
        }
      }
      if (hasUtm) {
        posthog.setPersonProperties(setProps, setOnceProps);
      }
    } catch (err) {
      // Non-fatal — keep onboarding instrumentation working even if URL parsing fails.
      // eslint-disable-next-line no-console
      console.warn('[Analytics] UTM persistence skipped:', err);
    }
  }, [posthog]);

  // Sync exam name when certificate changes
  useEffect(() => {
    if (!posthog || !selectedCertificate) return;
    posthog.register({ exam_name: GET_APP_NAME(selectedCertificate) });
  }, [posthog, selectedCertificate]);

  // Sync subscription status to PostHog as super + person property.
  // register() is local-only; setPersonProperties emits a $set, so only call it
  // when the value actually changes — otherwise every effect re-run sends one.
  useEffect(() => {
    if (!posthog || isSubscribe === undefined) return;
    const isPremium = !!isSubscribe;
    posthog.register({ is_premium: isPremium });
    if (lastIsPremiumRef.current !== isPremium) {
      posthog.setPersonProperties({ is_premium: isPremium });
      lastIsPremiumRef.current = isPremium;
    }
  }, [posthog, isSubscribe]);

  // Fallback capture for when helpers aren't ready yet
  const capture = useCallback((eventName, properties = {}) => {
    if (posthog) {
      posthog.capture(eventName, properties);
    }
  }, [posthog]);

  // Core tracking
  const track = useCallback((eventName, properties = {}) => {
    capture(eventName, properties);
  }, [capture]);

  const trackEvent = track; // alias

  // Identify user (call on login)
  const identify = useCallback((userId, traits = {}) => {
    if (posthog) {
      posthog.identify(userId, traits);
    }
  }, [posthog]);

  // Update super properties when user or exam changes
  const updateUserInfo = useCallback((user) => {
    if (posthog && user?.uid && lastIdentifiedUidRef.current !== user.uid) {
      posthog.identify(user.uid, {
        email: user.email,
        app_name: BUNDLE_APP_NAME,
      });
      lastIdentifiedUidRef.current = user.uid;
    }
  }, [posthog]);

  const updateExamName = useCallback((selectedCertificate) => {
    if (posthog) {
      posthog.register({
        exam_name: GET_APP_NAME(selectedCertificate),
      });
    }
  }, [posthog]);

  const updateSubscriptionStatus = useCallback((isPremium) => {
    if (!posthog) return;
    // Super property — attached to every event (local, no event emitted)
    posthog.register({ is_premium: isPremium });
    // Person property — emits a $set, so only send when the value changes
    if (lastIsPremiumRef.current !== isPremium) {
      posthog.setPersonProperties({ is_premium: isPremium });
      lastIsPremiumRef.current = isPremium;
    }
  }, [posthog]);

  const resetUser = useCallback(() => {
    if (posthog) {
      posthog.reset();
    }
  }, [posthog]);

  // All typed helpers wrapped in useCallback for stable references
  const trackSignup = useCallback((method, properties = {}) => {
    helpersRef.current?.trackSignup(method, properties);
  }, []);

  const trackLogin = useCallback((method, properties = {}) => {
    helpersRef.current?.trackLogin(method, properties);
  }, []);

  const trackLogout = useCallback((properties = {}) => {
    helpersRef.current?.trackLogout(properties);
  }, []);

  const trackPasswordReset = useCallback((properties = {}) => {
    helpersRef.current?.trackPasswordReset(properties);
  }, []);

  const trackOnboardingStep = useCallback((step, properties = {}) => {
    helpersRef.current?.trackOnboardingStep(step, properties);
  }, []);

  const trackExamSelection = useCallback((exam, properties = {}) => {
    helpersRef.current?.trackExamSelection(exam, properties);
  }, []);

  const trackAssessmentStart = useCallback((properties = {}) => {
    helpersRef.current?.trackAssessmentStart(properties);
  }, []);

  const trackAssessmentComplete = useCallback((score, properties = {}) => {
    helpersRef.current?.trackAssessmentComplete(score, properties);
  }, []);

  const trackOnboardingComplete = useCallback((properties = {}) => {
    helpersRef.current?.trackOnboardingComplete(properties);
  }, []);

  // Diagnostic flow
  const trackDiagnosticStartClicked = useCallback((exam, properties = {}) => {
    helpersRef.current?.trackDiagnosticStartClicked(exam, properties);
  }, []);

  const trackDiagnosticSkipped = useCallback((properties = {}) => {
    helpersRef.current?.trackDiagnosticSkipped(properties);
  }, []);

  const trackDiagnosticQuestionViewed = useCallback((questionIndex, properties = {}) => {
    helpersRef.current?.trackDiagnosticQuestionViewed(questionIndex, properties);
  }, []);

  const trackDiagnosticQuestionAnswered = useCallback((questionIndex, properties = {}) => {
    helpersRef.current?.trackDiagnosticQuestionAnswered(questionIndex, properties);
  }, []);

  const trackDiagnosticNavigation = useCallback((direction, fromIndex, toIndex, properties = {}) => {
    helpersRef.current?.trackDiagnosticNavigation(direction, fromIndex, toIndex, properties);
  }, []);

  const trackDiagnosticEndTestTapped = useCallback((properties = {}) => {
    helpersRef.current?.trackDiagnosticEndTestTapped(properties);
  }, []);

  const trackDiagnosticResultsViewed = useCallback((properties = {}) => {
    helpersRef.current?.trackDiagnosticResultsViewed(properties);
  }, []);

  const trackDiagnosticReviewFilterTapped = useCallback((filter, properties = {}) => {
    helpersRef.current?.trackDiagnosticReviewFilterTapped(filter, properties);
  }, []);

  const trackDiagnosticTopicTapped = useCallback((topicName, properties = {}) => {
    helpersRef.current?.trackDiagnosticTopicTapped(topicName, properties);
  }, []);

  const trackDiagnosticCommunityModalOpened = useCallback((properties = {}) => {
    helpersRef.current?.trackDiagnosticCommunityModalOpened(properties);
  }, []);

  const trackDiagnosticStartJourneyClicked = useCallback((properties = {}) => {
    helpersRef.current?.trackDiagnosticStartJourneyClicked(properties);
  }, []);

  const trackDiagnosticSignupCardViewed = useCallback((properties = {}) => {
    helpersRef.current?.trackDiagnosticSignupCardViewed(properties);
  }, []);

  const trackSignupCtaTapped = useCallback((source, properties = {}) => {
    helpersRef.current?.trackSignupCtaTapped(source, properties);
  }, []);

  const trackExitSheetShown = useCallback((properties = {}) => {
    helpersRef.current?.trackExitSheetShown(properties);
  }, []);

  const trackPostReviewCardShown = useCallback((properties = {}) => {
    helpersRef.current?.trackPostReviewCardShown(properties);
  }, []);

  // Sign-in / signup modal
  const trackSigninModalShown = useCallback((properties = {}) => {
    helpersRef.current?.trackSigninModalShown(properties);
  }, []);

  const trackSigninModalDismissed = useCallback((reason, properties = {}) => {
    helpersRef.current?.trackSigninModalDismissed(reason, properties);
  }, []);

  const trackAuthMethodSelected = useCallback((method, screen, properties = {}) => {
    helpersRef.current?.trackAuthMethodSelected(method, screen, properties);
  }, []);

  const trackSigninModalSignupLinkTapped = useCallback((properties = {}) => {
    helpersRef.current?.trackSigninModalSignupLinkTapped(properties);
  }, []);

  const trackSigninModalDiagnosticLinkTapped = useCallback((properties = {}) => {
    helpersRef.current?.trackSigninModalDiagnosticLinkTapped(properties);
  }, []);

  const trackSigninModalForgotPasswordTapped = useCallback((properties = {}) => {
    helpersRef.current?.trackSigninModalForgotPasswordTapped(properties);
  }, []);

  const trackSigninModalLearnMoreTapped = useCallback((properties = {}) => {
    helpersRef.current?.trackSigninModalLearnMoreTapped(properties);
  }, []);

  // Auth failures
  const trackLoginFailed = useCallback((method, errorMessage, properties = {}) => {
    helpersRef.current?.trackLoginFailed(method, errorMessage, properties);
  }, []);

  const trackSignupFailed = useCallback((method, errorMessage, properties = {}) => {
    helpersRef.current?.trackSignupFailed(method, errorMessage, properties);
  }, []);

  const trackStudySessionStart = useCallback((topic, properties = {}) => {
    helpersRef.current?.trackStudySessionStart(topic, properties);
  }, []);

  const trackStudySessionComplete = useCallback((topic, duration, questionsAnswered, properties = {}) => {
    helpersRef.current?.trackStudySessionComplete(topic, duration, questionsAnswered, properties);
  }, []);

  const trackQuestionBookmark = useCallback((questionId, action, properties = {}) => {
    helpersRef.current?.trackQuestionBookmark(questionId, action, properties);
  }, []);

  const trackStudyToolSelected = useCallback((toolName, properties = {}) => {
    helpersRef.current?.trackStudyToolSelected(toolName, properties);
  }, []);

  const trackTestStart = useCallback((testType, testId, properties = {}) => {
    helpersRef.current?.trackTestStart(testType, testId, properties);
  }, []);

  const trackTestComplete = useCallback((testType, testId, score, duration, properties = {}) => {
    helpersRef.current?.trackTestComplete(testType, testId, score, duration, properties);
  }, []);

  const trackTestAbandon = useCallback((testType, testId, reason, properties = {}) => {
    helpersRef.current?.trackTestAbandon(testType, testId, reason, properties);
  }, []);

  const trackFlashcardSessionStart = useCallback((properties = {}) => {
    helpersRef.current?.trackFlashcardSessionStart(properties);
  }, []);

  const trackFlashcardSessionEnd = useCallback((duration, count, properties = {}) => {
    helpersRef.current?.trackFlashcardSessionEnd(duration, count, properties);
  }, []);

  const trackSubscriptionView = useCallback((plan, properties = {}) => {
    helpersRef.current?.trackSubscriptionView(plan, properties);
  }, []);

  const trackSubscriptionOfferViewed = useCallback((properties = {}) => {
    helpersRef.current?.trackSubscriptionOfferViewed(properties);
  }, []);

  const trackSubscriptionStart = useCallback((plan, properties = {}) => {
    helpersRef.current?.trackSubscriptionStart(plan, properties);
  }, []);

  const trackSubscriptionCancel = useCallback((plan, properties = {}) => {
    helpersRef.current?.trackSubscriptionCancel(plan, properties);
  }, []);

  const trackCancelReasonSubmitted = useCallback((reason, properties = {}) => {
    helpersRef.current?.trackCancelReasonSubmitted(reason, properties);
  }, []);

  const trackCancelIntent = useCallback((reason, properties = {}) => {
    helpersRef.current?.trackCancelIntent(reason, properties);
  }, []);

  const trackPassStoryModalShown = useCallback((properties = {}) => {
    helpersRef.current?.trackPassStoryModalShown(properties);
  }, []);

  const trackPassStorySubmitted = useCallback((properties = {}) => {
    helpersRef.current?.trackPassStorySubmitted(properties);
  }, []);

  const trackPassStorySkipped = useCallback((properties = {}) => {
    helpersRef.current?.trackPassStorySkipped(properties);
  }, []);

  const trackFeedbackSubmitted = useCallback((properties = {}) => {
    helpersRef.current?.trackFeedbackSubmitted(properties);
  }, []);

  const trackWhyLeftShown = useCallback((properties = {}) => {
    helpersRef.current?.trackWhyLeftShown(properties);
  }, []);

  const trackWhyLeftSubmitted = useCallback((reason, properties = {}) => {
    helpersRef.current?.trackWhyLeftSubmitted(reason, properties);
  }, []);

  const trackWhyLeftDismissed = useCallback((properties = {}) => {
    helpersRef.current?.trackWhyLeftDismissed(properties);
  }, []);

  const trackWinbackBannerShown = useCallback((properties = {}) => {
    helpersRef.current?.trackWinbackBannerShown(properties);
  }, []);

  const trackWinbackBannerClicked = useCallback((properties = {}) => {
    helpersRef.current?.trackWinbackBannerClicked(properties);
  }, []);

  const trackWinbackCheckoutStarted = useCallback((properties = {}) => {
    helpersRef.current?.trackWinbackCheckoutStarted(properties);
  }, []);

  const trackPurchaseIntent = useCallback((planName, properties = {}) => {
    helpersRef.current?.trackPurchaseIntent(planName, properties);
  }, []);

  const trackPurchaseComplete = useCallback((planName, amount, properties = {}) => {
    helpersRef.current?.trackPurchaseComplete(planName, amount, properties);
  }, []);

  const trackPromoCodeApplied = useCallback((promoCode, properties = {}) => {
    helpersRef.current?.trackPromoCodeApplied(promoCode, properties);
  }, []);

  const trackPromoCodeFailed = useCallback((attemptedCode, properties = {}) => {
    helpersRef.current?.trackPromoCodeFailed(attemptedCode, properties);
  }, []);

  const trackLicenseCodeActivated = useCallback((properties = {}) => {
    helpersRef.current?.trackLicenseCodeActivated(properties);
  }, []);

  const trackCustomTestCreate = useCallback((domainCount, questionCount, properties = {}) => {
    helpersRef.current?.trackCustomTestCreate(domainCount, questionCount, properties);
  }, []);

  const trackProfileUpdate = useCallback((field, properties = {}) => {
    helpersRef.current?.trackProfileUpdate(field, properties);
  }, []);

  const trackSearch = useCallback((query, resultsCount, properties = {}) => {
    helpersRef.current?.trackSearch(query, resultsCount, properties);
  }, []);

  const trackShare = useCallback((content, platform, properties = {}) => {
    helpersRef.current?.trackShare(content, platform, properties);
  }, []);

  const trackArticleView = useCallback((articleId, properties = {}) => {
    helpersRef.current?.trackArticleView(articleId, properties);
  }, []);

  const trackArticleBookmark = useCallback((articleId, action, properties = {}) => {
    helpersRef.current?.trackArticleBookmark(articleId, action, properties);
  }, []);

  const trackArticleReaction = useCallback((articleId, action, properties = {}) => {
    helpersRef.current?.trackArticleReaction(articleId, action, properties);
  }, []);

  // Metered article unlocks (prompt/unlock share article_id + surface)
  const trackArticleProgress = useCallback((articleId, progress, properties = {}) => {
    helpersRef.current?.trackArticleProgress(articleId, progress, properties);
  }, []);

  const trackArticleUnlock = useCallback((articleId, properties = {}) => {
    helpersRef.current?.trackArticleUnlock(articleId, properties);
  }, []);

  const trackArticleUnlockPromptShown = useCallback((articleId, properties = {}) => {
    helpersRef.current?.trackArticleUnlockPromptShown(articleId, properties);
  }, []);

  const trackArticleMeterExhaustedShown = useCallback((properties = {}) => {
    helpersRef.current?.trackArticleMeterExhaustedShown(properties);
  }, []);

  const trackArticleInterstitialShown = useCallback((articleId, properties = {}) => {
    helpersRef.current?.trackArticleInterstitialShown(articleId, properties);
  }, []);

  const trackArticleInterstitialUpsellClicked = useCallback((articleId, properties = {}) => {
    helpersRef.current?.trackArticleInterstitialUpsellClicked(articleId, properties);
  }, []);

  const trackArticleReadingResumed = useCallback((articleId, properties = {}) => {
    helpersRef.current?.trackArticleReadingResumed(articleId, properties);
  }, []);

  const trackArticlesRecommendedClicked = useCallback((domainId, properties = {}) => {
    helpersRef.current?.trackArticlesRecommendedClicked(domainId, properties);
  }, []);

  // QOTD
  const trackQOTDView = useCallback((properties = {}) => {
    helpersRef.current?.trackQOTDView(properties);
  }, []);

  const trackQOTDAnswer = useCallback((isCorrect, properties = {}) => {
    helpersRef.current?.trackQOTDAnswer(isCorrect, properties);
  }, []);

  const trackQOTDSkipped = useCallback((properties = {}) => {
    helpersRef.current?.trackQOTDSkipped(properties);
  }, []);

  const trackAccountDeletionAttempted = useCallback((properties = {}) => {
    helpersRef.current?.trackAccountDeletionAttempted(properties);
  }, []);

  const trackAppError = useCallback((errorType, errorMessage, properties = {}) => {
    helpersRef.current?.trackAppError(errorType, errorMessage, properties);
  }, []);

  const trackPageLoadTime = useCallback((loadTime, properties = {}) => {
    helpersRef.current?.trackPageLoadTime(loadTime, properties);
  }, []);

  const trackEngagement = useCallback((action, duration, properties = {}) => {
    helpersRef.current?.trackEngagement(action, duration, properties);
  }, []);

  const trackPageView = useCallback((pagePath, properties = {}) => {
    helpersRef.current?.trackPageView(pagePath, properties);
  }, []);

  const trackNavigation = useCallback((from, to, properties = {}) => {
    helpersRef.current?.trackNavigation(from, to, properties);
  }, []);

  const value = useMemo(() => ({
    // Core
    track,
    trackEvent,
    identify,
    resetUser,

    // Authentication
    trackSignup,
    trackLogin,
    trackLogout,
    trackPasswordReset,

    // Onboarding
    trackOnboardingStep,
    trackExamSelection,
    trackAssessmentStart,
    trackAssessmentComplete,
    trackOnboardingComplete,

    // Diagnostic flow
    trackDiagnosticStartClicked,
    trackDiagnosticSkipped,
    trackDiagnosticQuestionViewed,
    trackDiagnosticQuestionAnswered,
    trackDiagnosticNavigation,
    trackDiagnosticEndTestTapped,
    trackDiagnosticResultsViewed,
    trackDiagnosticReviewFilterTapped,
    trackDiagnosticTopicTapped,
    trackDiagnosticCommunityModalOpened,
    trackDiagnosticStartJourneyClicked,
    trackDiagnosticSignupCardViewed,
    trackSignupCtaTapped,
    trackExitSheetShown,
    trackPostReviewCardShown,

    // Sign-in / signup modal
    trackSigninModalShown,
    trackSigninModalDismissed,
    trackAuthMethodSelected,
    trackSigninModalSignupLinkTapped,
    trackSigninModalDiagnosticLinkTapped,
    trackSigninModalForgotPasswordTapped,
    trackSigninModalLearnMoreTapped,

    // Auth failures
    trackLoginFailed,
    trackSignupFailed,

    // Study
    trackStudySessionStart,
    trackStudySessionComplete,
    trackQuestionBookmark,
    trackStudyToolSelected,

    // Tests
    trackTestStart,
    trackTestComplete,
    trackTestAbandon,

    // Flashcards
    trackFlashcardSessionStart,
    trackFlashcardSessionEnd,

    // Subscription & Purchase
    trackSubscriptionView,
    trackSubscriptionOfferViewed,
    trackSubscriptionStart,
    trackSubscriptionCancel,
    trackCancelReasonSubmitted,
    trackCancelIntent,
    trackPassStoryModalShown,
    trackPassStorySubmitted,
    trackPassStorySkipped,
    trackFeedbackSubmitted,
    trackWhyLeftShown,
    trackWhyLeftSubmitted,
    trackWhyLeftDismissed,
    trackWinbackBannerShown,
    trackWinbackBannerClicked,
    trackWinbackCheckoutStarted,
    trackPurchaseIntent,
    trackPurchaseComplete,
    trackPromoCodeApplied,
    trackPromoCodeFailed,
    trackLicenseCodeActivated,

    // Features
    trackCustomTestCreate,
    trackProfileUpdate,
    trackSearch,
    trackShare,
    trackAccountDeletionAttempted,

    // Articles
    trackArticleView,
    trackArticleBookmark,
    trackArticleReaction,
    trackArticleProgress,
    trackArticleUnlock,
    trackArticleUnlockPromptShown,
    trackArticleMeterExhaustedShown,
    trackArticleInterstitialShown,
    trackArticleInterstitialUpsellClicked,
    trackArticleReadingResumed,
    trackArticlesRecommendedClicked,

    // QOTD
    trackQOTDView,
    trackQOTDAnswer,
    trackQOTDSkipped,

    // Errors & Performance
    trackAppError,
    trackPageLoadTime,
    trackEngagement,
    trackPageView,
    trackNavigation,

    // Utility
    updateUserInfo,
    updateExamName,
    updateSubscriptionStatus,
  }), [
    track, trackEvent, identify, resetUser,
    trackSignup, trackLogin, trackLogout, trackPasswordReset,
    trackOnboardingStep, trackExamSelection, trackAssessmentStart, trackAssessmentComplete, trackOnboardingComplete,
    trackDiagnosticStartClicked, trackDiagnosticSkipped, trackDiagnosticQuestionViewed,
    trackDiagnosticQuestionAnswered, trackDiagnosticNavigation, trackDiagnosticEndTestTapped,
    trackDiagnosticResultsViewed, trackDiagnosticReviewFilterTapped, trackDiagnosticTopicTapped,
    trackDiagnosticCommunityModalOpened, trackDiagnosticStartJourneyClicked,
    trackDiagnosticSignupCardViewed,
    trackSignupCtaTapped, trackExitSheetShown, trackPostReviewCardShown,
    trackSigninModalShown, trackSigninModalDismissed, trackAuthMethodSelected,
    trackSigninModalSignupLinkTapped, trackSigninModalDiagnosticLinkTapped,
    trackSigninModalForgotPasswordTapped, trackSigninModalLearnMoreTapped,
    trackLoginFailed, trackSignupFailed,
    trackStudySessionStart, trackStudySessionComplete, trackQuestionBookmark, trackStudyToolSelected,
    trackTestStart, trackTestComplete, trackTestAbandon,
    trackFlashcardSessionStart, trackFlashcardSessionEnd,
    trackSubscriptionView, trackSubscriptionOfferViewed, trackSubscriptionStart, trackSubscriptionCancel, trackCancelReasonSubmitted,
    trackCancelIntent, trackPassStoryModalShown, trackPassStorySubmitted, trackPassStorySkipped,
    trackFeedbackSubmitted, trackWhyLeftShown, trackWhyLeftSubmitted, trackWhyLeftDismissed,
    trackWinbackBannerShown, trackWinbackBannerClicked, trackWinbackCheckoutStarted,
    trackPurchaseIntent, trackPurchaseComplete,
    trackPromoCodeApplied, trackPromoCodeFailed, trackLicenseCodeActivated,
    trackCustomTestCreate, trackProfileUpdate, trackSearch, trackShare, trackAccountDeletionAttempted,
    trackArticleView, trackArticleBookmark, trackArticleReaction,
    trackArticleProgress, trackArticleUnlock, trackArticleUnlockPromptShown,
    trackArticleMeterExhaustedShown, trackArticleInterstitialShown, trackArticleInterstitialUpsellClicked,
    trackArticleReadingResumed, trackArticlesRecommendedClicked,
    trackQOTDView, trackQOTDAnswer, trackQOTDSkipped,
    trackAppError, trackPageLoadTime, trackEngagement, trackPageView, trackNavigation,
    updateUserInfo, updateExamName, updateSubscriptionStatus,
  ]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Custom hook to use analytics
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
